import { createContext, useContext, useState } from 'react';
import { cn } from 'utils';

// Tab Context
const TabsContext = createContext({});

// Main Tabs Container
export function Tabs({ value, onValueChange, defaultValue, children, className }) {
  const [selectedTab, setSelectedTab] = useState(value || defaultValue || '');

  const handleTabChange = (tabValue) => {
    if (onValueChange) {
      onValueChange(tabValue);
    } else {
      setSelectedTab(tabValue);
    }
  };

  const currentTab = value ?? selectedTab;

  return (
    <TabsContext.Provider value={{ selectedTab: currentTab, onTabChange: handleTabChange }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// Tab List (Navigation)
export function TabsList({ children, className }) {
  return (
    <div className={cn(
      'inline-flex h-10 items-center justify-center rounded-md bg-gray-100 dark:bg-dark-700 p-1 text-gray-500 dark:text-dark-300',
      className
    )}>
      {children}
    </div>
  );
}

// Individual Tab Trigger
export function TabsTrigger({ value, children, className, disabled }) {
  const { selectedTab, onTabChange } = useContext(TabsContext);
  const isSelected = selectedTab === value;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onTabChange(value)}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        isSelected
          ? 'bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm'
          : 'text-gray-600 dark:text-dark-300 hover:text-gray-900 dark:hover:text-dark-100',
        className
      )}
    >
      {children}
    </button>
  );
}

// Tab Content Container
export function TabsContent({ value, children, className }) {
  const { selectedTab } = useContext(TabsContext);
  
  if (selectedTab !== value) {
    return null;
  }

  return (
    <div className={cn('mt-4 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2', className)}>
      {children}
    </div>
  );
}

// Export everything as default for easier imports
export default {
  Root: Tabs,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};